/**
 * What the player may legally attempt right now.
 *
 * Every judgment here is made by asking the game engine's own rule functions,
 * never by restating a rule in the browser. The server is still the authority -
 * it re-validates everything - but a control that cannot lead anywhere is
 * disabled rather than offered, so keyboard and screen-reader players learn
 * what is legal from focusability instead of from color.
 */

import {
    type Card,
    type GameView,
    type Meld,
    type MeldableRank,
    type PlayerView,
    type Rank,
    type RuleErrorCode,
    type Team,
    addToMeld,
    createMeld,
    findMeld,
    getCardsValue,
    getCardValue,
    isMeldableRank,
    isWild
} from "@ryzun/haf-saloon-game-engine";

export interface TurnOptions {
    readonly self: PlayerView | null;
    readonly team: Team | null;
    readonly activeHand: readonly Card[];
    readonly isYourTurn: boolean;
    readonly isDrawPhase: boolean;
    readonly canDrawStock: boolean;
    readonly canTakeDiscard: boolean;
    /** Whether the selection can join the melds waiting to be laid down. */
    readonly canStageMeld: boolean;
    /** Whether the staged melds can be played, opener included. */
    readonly canLayDown: boolean;
    readonly canDiscard: boolean;
    /**
     * Cards in the active hand that may be picked right now. Before the draw
     * that is only the ones that could take the pile, and none when nothing in
     * hand could.
     */
    readonly selectableIds: ReadonlySet<string>;
    /** The card a click on the discard pile would throw, or null if none would. */
    readonly discardCard: Card | null;
    /** Face value of everything staged, which is what the opener is measured against. */
    readonly stagedValue: number;
    /** Ranks of team melds the current selection could be added to. */
    readonly addableRanks: readonly MeldableRank[];
    /** Why taking the pile is unavailable, for the hint under the controls. */
    readonly discardHint: string | null;
    /** Why the selection cannot be played yet, so a dead button explains itself. */
    readonly playHint: string | null;
}

/** Where a playable card sits among its same-rank playable neighbors in the fan. */
export type RunPosition = "end" | "mid" | "solo" | "start";

/** A meld the player has built but not yet committed to the table. */
export interface StagedMeld {
    readonly rank: MeldableRank;
    readonly cardIds: readonly string[];
    readonly value: number;
}

/** A staged meld resolved back to its cards, so it can be drawn on the felt. */
export interface StagedTile {
    readonly rank: MeldableRank;
    readonly cards: readonly Card[];
    readonly value: number;
}

/**
 * The lay-down controls a team's own meld row carries: the tile that opens a
 * new meld, the melds waiting on an opener, and what settles them.
 *
 * Melding used to happen in a button bar underneath the table, which meant the
 * cards went somewhere the cards were not. This is the same set of actions,
 * addressed to the place on the felt where they land; the row raises an event
 * for each of them rather than holding callbacks.
 */
export interface LayDownSurface {
    /** Whether the selection would open a meld the team does not already have. */
    readonly canCreate: boolean;
    readonly staged: readonly StagedTile[];
    readonly stagedValue: number;
    /** Whether the team still has to clear the round minimum to get in (§5). */
    readonly opensRound: boolean;
    readonly roundMinimum: number;
    readonly canLayDown: boolean;
}

/**
 * Resolves staged card ids back to the cards themselves. Ids that are no longer
 * held are dropped: the server replaces the hand on every update, so a staged
 * meld can be outlived by the cards it was built from.
 */
export function toStagedTiles(
    hand: readonly Card[],
    staged: readonly StagedMeld[]
): readonly StagedTile[] {
    return staged.map((entry) => ({
        rank: entry.rank,
        value: entry.value,
        cards: entry.cardIds
            .map((id) => hand.find((card) => card.id === id))
            .filter((card) => card !== undefined)
    }));
}

/** The pile the player is drawing into and playing from: the Foot once opened (§5). */
export function getActiveHand(player: PlayerView | null): readonly Card[] {
    if (player === null) {
        return [];
    }

    return (player.inFoot ? player.foot : player.hand) ?? [];
}

export function findSelf(view: GameView): PlayerView | null {
    return view.players.find((player) => player.id === view.viewerId) ?? null;
}

export function findTeam(view: GameView, player: PlayerView | null): Team | null {
    if (player === null) {
        return null;
    }

    return view.teams.find((team) => team.id === player.teamId) ?? null;
}

function selectCards(hand: readonly Card[], selectedIds: readonly string[]): readonly Card[] {
    return hand.filter((card) => selectedIds.includes(card.id));
}

/**
 * Whether a set of new melds clears the round's opening requirement. Only
 * applies before a team is in the game (§5).
 */
function clearsMinimum(team: Team | null, view: GameView, groups: readonly (readonly Card[])[]): boolean {
    if (team === null) {
        return false;
    }

    if (team.inGame) {
        return true;
    }

    const laid = groups.reduce((total, group) => total + getCardsValue(group), 0);

    return laid >= view.roundMinimum;
}

function countNaturals(cards: readonly Card[], rank: Rank): number {
    return cards.filter((card) => !isWild(card) && card.rank === rank).length;
}

function rankAlreadyMelded(team: Team | null, meld: Meld): boolean {
    return team !== null && findMeld(team.melds, meld.rank) !== undefined;
}

/**
 * Whether the selection plus the discard top opens a meld the player may take
 * the pile with. The engine's own `createMeld` decides whether the cards form a
 * meld at all; the extra checks are the pile-specific conditions from §5.
 */
function checkDiscardDraw(
    view: GameView,
    team: Team | null,
    selection: readonly Card[]
): { readonly allowed: boolean; readonly hint: string | null; } {
    const top = view.discardTop;

    if (top === null) {
        return { allowed: false, hint: "the pile is empty" };
    }

    if (view.discardFrozen) {
        return { allowed: false, hint: "the pile is frozen" };
    }

    // Wilds can ride along, but the engine only lets the pile go to a player
    // already holding two naturals of the top card's rank (§5).
    if (countNaturals(selection, top.rank) < 2) {
        return { allowed: false, hint: `select two ${top.rank}s to take the pile` };
    }

    const meld = createMeld([...selection, top]);

    if (!meld.ok) {
        return { allowed: false, hint: "not a meld with the top card" };
    }

    if (meld.value.rank !== top.rank) {
        return { allowed: false, hint: `the meld must be ${top.rank}s` };
    }

    if (rankAlreadyMelded(team, meld.value)) {
        return { allowed: false, hint: `${top.rank}s are already melded` };
    }

    if (!clearsMinimum(team, view, [meld.value.cards])) {
        return { allowed: false, hint: `short of the ${view.roundMinimum} opener` };
    }

    return { allowed: true, hint: null };
}

/**
 * The cards in hand that could take the pile with the discard top, and why
 * there are none when there are none.
 *
 * Every copy of the top card's rank goes in, plus the wilds worth the most, one
 * more at a time: more naturals never hurt a meld, and the highest wilds are
 * what get an opener over the bar soonest. If none of those takes the pile, no
 * other pick from the hand would either.
 */
function findPileCards(
    view: GameView,
    team: Team | null,
    hand: readonly Card[]
): { readonly cards: readonly Card[]; readonly hint: string | null; } {
    const top = view.discardTop;

    if (top === null || view.discardFrozen) {
        return { cards: [], hint: checkDiscardDraw(view, team, []).hint };
    }

    const naturals = hand.filter((card) => !isWild(card) && card.rank === top.rank);

    if (naturals.length < 2) {
        return { cards: [], hint: `need two ${top.rank}s to take the pile` };
    }

    const wilds = hand.filter(isWild).toSorted((a, b) => getCardValue(b) - getCardValue(a));
    const attempts = Array.from({ length: wilds.length + 1 }, (_, count) => [...naturals, ...wilds.slice(0, count)]);

    if (attempts.some((selection) => checkDiscardDraw(view, team, selection).allowed)) {
        return { cards: [...naturals, ...wilds], hint: null };
    }

    // The richest attempt that is a meld at all says why it still falls short,
    // which beats a bare "no" when the answer is the opener or a doubled rank.
    const closest = attempts.findLast((selection) => createMeld([...selection, top]).ok);

    return {
        cards: [],
        hint: closest === undefined
            ? `no meld with the ${top.rank}`
            : checkDiscardDraw(view, team, closest).hint
    };
}

function getAddableRanks(team: Team | null, selection: readonly Card[]): readonly MeldableRank[] {
    if (team === null || !team.inGame || selection.length === 0) {
        return [];
    }

    return team.melds
        .filter((meld) => addToMeld(meld, selection).ok)
        .map((meld) => meld.rank);
}

/**
 * Whether the selection can join the lay-down, and why not when it cannot.
 *
 * The round minimum is deliberately not checked here: an opener is often two or
 * three melds that only clear the bar together (§5), so it is measured against
 * everything staged rather than against each meld as it is built.
 */
function checkStageMeld(
    team: Team | null,
    selection: readonly Card[],
    staged: readonly StagedMeld[]
): { readonly allowed: boolean; readonly hint: string | null; } {
    if (selection.length === 0) {
        return { allowed: false, hint: null };
    }

    const meld = createMeld(selection);

    if (!meld.ok) {
        return { allowed: false, hint: describeMeldFailure(meld.error.code, selection) };
    }

    if (rankAlreadyMelded(team, meld.value)) {
        return {
            allowed: false,
            hint: `your team already has ${meld.value.rank}s down - click it on the table to add these`
        };
    }

    if (staged.some((entry) => entry.rank === meld.value.rank)) {
        return { allowed: false, hint: `${meld.value.rank}s are already in the lay-down` };
    }

    return { allowed: true, hint: null };
}

/** Turns an engine rejection into something worth reading under a dead button. */
function describeMeldFailure(code: RuleErrorCode, selection: readonly Card[]): string {
    switch (code) {
        case "meld-too-small": {
            return selection.length === 1
                ? "a meld needs three of a rank, or two plus a wild"
                : "two of a rank needs a wild alongside it to be a meld";
        }

        case "meld-mixed-ranks": {
            return "a meld has to be all one rank, plus any wilds";
        }

        case "meld-too-many-wilds":
        case "meld-not-enough-naturals": {
            return "a meld cannot hold more wilds than ranked cards";
        }

        case "meld-unmeldable-rank": {
            return "threes cannot be melded - they can only be discarded";
        }

        default: {
            return "those cards do not make a meld";
        }
    }
}

/**
 * Why the selection cannot join a particular meld on the table.
 *
 * Clicking a meld you cannot play into used to do nothing visible, which reads
 * as the click having been missed rather than refused. As everywhere else here,
 * the judgment is the engine's - this only puts words to the code it returns.
 */
export function describeAddFailure(
    view: GameView,
    selectedIds: readonly string[],
    rank: MeldableRank
): string {
    const self = findSelf(view);
    const team = findTeam(view, self);
    const selection = selectCards(getActiveHand(self), selectedIds);
    const meld = team === null ? undefined : findMeld(team.melds, rank);

    if (meld === undefined) {
        return `your team has no ${rank}s down`;
    }

    if (team !== null && !team.inGame) {
        return `your team is not in the game yet - lay down ${view.roundMinimum} points first`;
    }

    const result = addToMeld(meld, selection);

    if (result.ok) {
        return "";
    }

    switch (result.error.code) {
        case "nothing-played": {
            return "pick the cards you want to add first";
        }

        case "meld-mixed-ranks": {
            return `only ${rank}s and wild cards can join this meld`;
        }

        case "pure-book-locked": {
            return `the ${rank} book is pure - it cannot take wild cards now`;
        }

        case "meld-too-many-wilds":
        case "meld-not-enough-naturals": {
            return "that would leave the meld holding more wilds than ranked cards";
        }

        case "meld-unmeldable-rank": {
            return "threes cannot be melded - they can only be discarded";
        }

        default: {
            return "those cards cannot join this meld";
        }
    }
}

function describePlayBlock(
    stage: { readonly allowed: boolean; readonly hint: string | null; },
    staged: readonly StagedMeld[],
    shortfall: number
): string | null {
    if (shortfall > 0) {
        return `${shortfall} more points needed to get in the game - build another meld`;
    }

    // A selection that will not join the lay-down would otherwise be a discard,
    // and a discard ends the turn - which would throw the lay-down away.
    if (staged.length > 0 && stage.hint !== null) {
        return "lay down or clear your melds before discarding";
    }

    return stage.hint;
}

/**
 * The card a click on the discard pile would throw: the last one picked that is
 * still held. Picking a rank takes every copy of it, so a selection built for a
 * meld can be discarded from without being taken apart first.
 *
 * A wild alongside a rank is a meld being assembled, never a discard, so the
 * pile reaches past the wilds for the last ranked card. Throwing a wild away is
 * deliberate enough to be worth asking for: select it on its own, as with a
 * three.
 */
function findDiscardCard(hand: readonly Card[], selectedIds: readonly string[]): Card | null {
    const held = selectedIds
        .map((id) => hand.find((card) => card.id === id))
        .filter((card) => card !== undefined);

    return held.findLast((card) => !isWild(card)) ?? held.at(-1) ?? null;
}

/**
 * Adds the selection to the lay-down. The caller is expected to have checked
 * `canStageMeld`; an unmeldable selection is returned unchanged.
 */
export function stageSelection(
    hand: readonly Card[],
    selectedIds: readonly string[],
    staged: readonly StagedMeld[]
): readonly StagedMeld[] {
    const selection = selectCards(hand, selectedIds);
    const meld = createMeld(selection);

    if (!meld.ok || staged.some((entry) => entry.rank === meld.value.rank)) {
        return staged;
    }

    return [...staged, {
        rank: meld.value.rank,
        cardIds: selection.map((card) => card.id),
        value: getCardsValue(selection)
    }];
}

/** Card ids spoken for by the lay-down, so the hand can stop offering them. */
export function getStagedCardIds(staged: readonly StagedMeld[]): readonly string[] {
    return staged.flatMap((entry) => entry.cardIds);
}

export function getTurnOptions(
    view: GameView,
    selectedIds: readonly string[],
    staged: readonly StagedMeld[] = []
): TurnOptions {
    const self = findSelf(view);
    const team = findTeam(view, self);
    const activeHand = getActiveHand(self);
    const selection = selectCards(activeHand, selectedIds);
    const isYourTurn = view.status === "in-round" && view.turn?.playerId === view.viewerId;
    const isDrawPhase = isYourTurn && view.turn?.phase === "draw";
    const isPlayPhase = isYourTurn && view.turn?.phase === "play";
    const pile = checkDiscardDraw(view, team, selection);
    const pileCards = isDrawPhase ? findPileCards(view, team, activeHand) : { cards: [], hint: null };
    const stage = checkStageMeld(team, selection, staged);
    const stagedValue = staged.reduce((total, entry) => total + entry.value, 0);
    const opensRound = team !== null && !team.inGame;
    const addableRanks = isPlayPhase && staged.length === 0 ? getAddableRanks(team, selection) : [];
    const shortfall = opensRound && staged.length > 0
        ? Math.max(0, view.roundMinimum - stagedValue)
        : 0;

    // Staged melds are lost if the turn ends on a discard, so the lay-down has
    // to be settled one way or the other before the pile will take a card.
    //
    // A card that could join a team meld is still a legal discard. Throwing one
    // away is sometimes the better move - it keeps a pair in hand that a rank
    // already down would only dilute - and when it is a mistake, it is the
    // player's to make.
    const discardCard = isPlayPhase && staged.length === 0
        ? findDiscardCard(activeHand, selectedIds)
        : null;

    return {
        self,
        team,
        activeHand,
        isYourTurn,
        isDrawPhase,
        canDrawStock: isDrawPhase && view.stockCount > 0,
        canTakeDiscard: isDrawPhase && pile.allowed,
        canStageMeld: isPlayPhase && stage.allowed,
        canLayDown: isPlayPhase
            && staged.length > 0
            && (!opensRound || stagedValue >= view.roundMinimum),
        canDiscard: discardCard !== null,
        // Before the draw, a pick only ever means taking the pile - so the
        // hand is shut unless the pile is there to be taken.
        selectableIds: new Set((isPlayPhase ? activeHand : pileCards.cards).map((card) => card.id)),
        discardCard,
        stagedValue,
        addableRanks,
        discardHint: isDrawPhase ? pileCards.hint ?? pile.hint : null,
        // Melding and discarding are dead until the player has drawn, which is
        // the easiest way for a new player to get stuck: two disabled buttons
        // and no clue that the turn has not really started yet.
        playHint: isDrawPhase
            ? pileCards.cards.length > 0
                ? "draw before you can meld or discard - take two from the stock, or take the pile"
                : "draw before you can meld or discard - take two from the stock"
            : isPlayPhase
                ? describePlayBlock(stage, staged, shortfall)
                : null
    };
}

/**
 * Cards that could go somewhere this turn, either onto a meld the team already
 * holds or as part of a new meld with others of the same rank in hand. Used as
 * a shape cue on the card, never as the only signal.
 */
export function getPlayableCardIds(view: GameView): ReadonlySet<string> {
    const self = findSelf(view);
    const team = findTeam(view, self);
    const hand = getActiveHand(self);
    const playable = new Set<string>();

    if (team === null) {
        return playable;
    }

    const counts = new Map<string, number>();

    for (const card of hand) {
        if (isMeldableRank(card.rank)) {
            counts.set(card.rank, (counts.get(card.rank) ?? 0) + 1);
        }
    }

    for (const card of hand) {
        const joinable = team.melds.some((meld) => addToMeld(meld, [card]).ok);
        const groupable = isMeldableRank(card.rank) && (counts.get(card.rank) ?? 0) >= 3;

        if (joinable || groupable) {
            playable.add(card.id);
        }
    }

    return playable;
}

/**
 * Groups the playable cards of a hand, in display order, into runs of a rank,
 * so the fan can bracket each run as a unit rather than marking every card on
 * its own.
 *
 * Wilds are left out. A bracket says "these go down together", and a row of
 * wilds never does - each one joins some other rank's meld on its own.
 */
export function getPlayableRuns(
    cards: readonly Card[],
    playable: ReadonlySet<string>
): ReadonlyMap<string, RunPosition> {
    const keyAt = (index: number): string | null => {
        const card = index < 0 ? undefined : cards[index];

        if (card === undefined || !playable.has(card.id) || isWild(card)) {
            return null;
        }

        return card.rank;
    };

    return new Map(cards.flatMap((card, index): (readonly [string, RunPosition])[] => {
        const key = keyAt(index);

        if (key === null) {
            return [];
        }

        const joinsPrevious = keyAt(index - 1) === key;
        const joinsNext = keyAt(index + 1) === key;

        if (joinsPrevious) {
            return [[card.id, joinsNext ? "mid" : "end"]];
        }

        return [[card.id, joinsNext ? "start" : "solo"]];
    }));
}
